import React, { useState } from "react";
import { useLanguage } from "../../contexts/LanguageContext";
import QuickReplyListEditor from "./QuickReplyListEditor";
import PageContextsBrowser from "./PageContextsBrowser";
import PageContextsIO from "./PageContextsIO";
import DirtyDot from "./DirtyDot";
import UrlField from "./UrlField";
import { setOrDelete } from "./setOrDelete";
import { copyLocaleTabs, localeLabel } from "./widgetLocales";

const CopySection = ({
  copy,
  baseline,
  locale,
  locales,
  activeLocale,
  onActiveLocaleChange,
  onChange,
  chatId,
  hasUnsavedChanges,
  onImported,
  onUnsavedContextsChange,
}) => {
  const { translate } = useLanguage();
  const c = copy || {};

  // A contagem que o PageContextsIO mostra e com que confirma o import. Vem só do
  // PageContextsBrowser, que a lê do servidor e a acerta a cada criação e
  // remoção. O GET das definições já não traz os contextos, portanto até à
  // primeira leitura da lista a contagem não se sabe, e `null` diz isso mesmo.
  const [contextCount, setContextCount] = useState(null);
  // Muda depois de um import para remontar o browser: página 0, pesquisa limpa,
  // e a lista relida do servidor. O import substitui os contextos todos, portanto
  // os ids que o browser tinha em ecrã deixaram de existir.
  const [browserVersion, setBrowserVersion] = useState(0);

  const defaultLocale = locale || "pt";

  const handleImported = async (importedChatId) => {
    // Se a releitura da página falhar, o erro sobe até ao PageContextsIO, que
    // depende dele para não anunciar sucesso, e o browser não chega a remontar
    // porque o formulário inteiro sai do ecrã.
    await onImported(importedChatId);
    setBrowserVersion((v) => v + 1);
  };

  const set = (key, value) => onChange(setOrDelete(c, key, value));

  const tabs = copyLocaleTabs(defaultLocale, locales);
  // Qual é o separador aberto decide-se aqui dentro e não só na página: o
  // selector de idiomas está na mesma página, e tirar de lá um idioma (ou trocar
  // o de omissão) enquanto o separador dele está activo deixava o escolhido a
  // apontar para um separador que já não existe, com a escrita a ir parar a um
  // `translations` que ninguém volta a ver.
  const active = tabs.includes(activeLocale) ? activeLocale : tabs[0];
  const isDefault = active === defaultLocale;

  const translations = c.translations || {};
  // O separador por omissão escreve nos campos de topo; os outros escrevem
  // dentro do `translations`. É a mesma regra com que o `served_copy` do backend
  // os lê, e é por causa dela que um campo por traduzir chega ao widget ausente
  // — e não com o português lá dentro.
  const fields = isDefault ? c : translations[active] || {};
  const setField = (key, value) => {
    if (isDefault) return set(key, value);
    const entry = setOrDelete(translations[active] || {}, key, value);
    const next = { ...translations };
    // Um idioma que fique sem campos nenhuns sai do mapa: um `{}` lá dentro diz
    // "configurado com nada", que é precisamente o que impede a resolução de
    // cair para o genérico do idioma.
    if (Object.keys(entry).length) next[active] = entry;
    else delete next[active];
    onChange(setOrDelete(c, "translations", Object.keys(next).length ? next : null));
  };

  // Sem barra de separadores não há painel: um role="tabpanel" sozinho anuncia
  // uma estrutura que não está no ecrã.
  const panel =
    tabs.length > 1
      ? {
          role: "tabpanel",
          id: "copy-locale-panel",
          "aria-labelledby": `copy-locale-tab-${active}`,
        }
      : {};

  const isDirty = JSON.stringify(copy || {}) !== JSON.stringify(baseline || {});
  return (
    <div className="rounded-xl overflow-hidden"
      style={{ backgroundColor: "var(--color-surface)", border: "1px solid var(--color-border)" }}>
      <div className="px-6 py-4" style={{ borderBottom: "1px solid var(--color-border)" }}>
        <h2 className="flex items-center gap-2 text-base font-semibold" style={{ color: "var(--color-text-primary)" }}>
          {translate("copy") || "Copy"}
          {isDirty && <DirtyDot />}
        </h2>
      </div>
      <div className="px-6 py-5 space-y-4">
        {/* A persona fica de fora dos separadores: o assistente é o mesmo em
            todos os mercados que este widget serve. */}
        <div>
          <label className="block text-xs font-medium uppercase tracking-wide mb-2"
            style={{ color: "var(--color-text-primary)" }}>
            {translate("headerTitle") || "Header title"}
          </label>
          <input
            type="text"
            value={c.headerTitle || ""}
            onChange={(e) => set("headerTitle", e.target.value || null)}
            maxLength={80}
            className="field-input w-full"
            placeholder={translate("headerTitlePlaceholder") || "e.g. Virtual assistant"}
          />
        </div>
        <div>
          <label className="block text-xs font-medium uppercase tracking-wide mb-2"
            style={{ color: "var(--color-text-primary)" }}>
            {translate("botName") || "Bot name"}
          </label>
          <input
            type="text"
            value={c.botName || ""}
            onChange={(e) => set("botName", e.target.value || null)}
            maxLength={80}
            className="field-input w-full"
            placeholder={translate("botNamePlaceholder") || "e.g. Kodly"}
          />
        </div>
        <div>
          <UrlField
            label={translate("botAvatarUrl") || "Bot avatar URL"}
            value={c.botAvatarUrl}
            onChange={(v) => set("botAvatarUrl", v)}
            placeholder="https://..."
          />
        </div>

        {tabs.length > 1 && (
          <div
            role="tablist"
            aria-label={translate("copyLanguageTab") || "Language"}
            className="flex gap-6 border-b"
            style={{ borderColor: "var(--color-border)" }}
          >
            {tabs.map((loc) => {
              const selected = loc === active;
              return (
                <button
                  key={loc}
                  type="button"
                  role="tab"
                  id={`copy-locale-tab-${loc}`}
                  aria-selected={selected}
                  aria-controls="copy-locale-panel"
                  onClick={() => onActiveLocaleChange(loc)}
                  className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                    selected
                      ? "border-[var(--color-mint)] text-[var(--color-mint)]"
                      : "border-transparent hover:border-gray-300"
                  }`}
                  style={selected ? undefined : { color: "var(--color-text-secondary)" }}
                >
                  {localeLabel(loc)}
                </button>
              );
            })}
          </div>
        )}

        <div className="space-y-4" {...panel}>
          <div>
            <label className="block text-xs font-medium uppercase tracking-wide mb-2"
              style={{ color: "var(--color-text-primary)" }}>
              {translate("launcherTitle") || "Launcher title"}
            </label>
            <input
              type="text"
              value={fields.launcherTitle || ""}
              onChange={(e) => setField("launcherTitle", e.target.value || null)}
              className="field-input w-full"
              placeholder={translate("launcherTitlePlaceholder") || "e.g. Need help?"}
            />
          </div>
          <div>
            <label className="block text-xs font-medium uppercase tracking-wide mb-2"
              style={{ color: "var(--color-text-primary)" }}>
              {translate("welcomeTitle") || "Welcome title"}
            </label>
            <input
              type="text"
              value={fields.welcomeTitle || ""}
              onChange={(e) => setField("welcomeTitle", e.target.value || null)}
              className="field-input w-full"
              placeholder={translate("welcomeTitlePlaceholder") || "e.g. Olá! 👋"}
            />
          </div>
          <div>
            <label className="block text-xs font-medium uppercase tracking-wide mb-2"
              style={{ color: "var(--color-text-primary)" }}>
              {translate("welcomeDescription") || "Welcome description"}
            </label>
            <textarea
              value={fields.welcomeDescription || ""}
              onChange={(e) => setField("welcomeDescription", e.target.value || null)}
              rows={3}
              className="field-input resize-y w-full"
              placeholder={translate("welcomeDescriptionPlaceholder") || "e.g. Em que podemos ajudar?"}
            />
          </div>
          <div>
            <label className="block text-xs font-medium uppercase tracking-wide mb-2"
              style={{ color: "var(--color-text-primary)" }}>
              {translate("defaultQuickReplies") || "Default quick replies"}
            </label>
            <QuickReplyListEditor
              values={fields.welcomeQuickReplies}
              onChange={(v) => setField("welcomeQuickReplies", v)}
            />
          </div>
          <div>
            <label className="block text-xs font-medium uppercase tracking-wide mb-2"
              style={{ color: "var(--color-text-primary)" }}>
              {translate("priceInfoNotice") || "Price info notice"}
            </label>
            <textarea
              value={fields.priceInfoNotice || ""}
              onChange={(e) => setField("priceInfoNotice", e.target.value || null)}
              rows={4}
              className="field-input resize-y w-full"
              placeholder={translate("priceInfoNoticePlaceholder") || "e.g. Prices are valid online only..."}
            />
            <p className="mt-1 text-xs" style={{ color: "var(--color-text-muted)" }}>
              {translate("priceInfoNoticeHint") ||
                "Shown in a popover behind the (i) icon next to prices on product cards. Leave empty to hide the icon."}
            </p>
          </div>
        </div>

        {/* As replies por omissão ficaram acima, dentro dos separadores, e os
            contextos de página ficam aqui fora. Deixaram de ser uma família, e a
            régua à esquerda que os juntava saiu com a separação: o texto de uma
            reply muda com o idioma que o widget serve, e um contexto de página
            não tem idioma nenhum — é uma regra de URL, a mesma para todos os
            mercados. */}
        <div>
          {/* PageContextsIO owns this field's label, so the two controls can
              share the heading's row. The count and the cap stay above the
              editor: they are what tell the operator whether a bulk import is
              the right move. */}
          <PageContextsIO
            label={translate("pageContexts") || "Page-specific quick replies"}
            chatId={chatId}
            contextCount={contextCount}
            hasUnsavedChanges={hasUnsavedChanges}
            onImported={handleImported}
          />
          <PageContextsBrowser
            key={browserVersion}
            chatId={chatId}
            onCountChange={setContextCount}
            onUnsavedChange={onUnsavedContextsChange}
          />
          <p className="mt-1 text-xs" style={{ color: "var(--color-text-muted)" }}>
            {translate("pageContextsHint") ||
              "The first context in the list whose patterns match the page wins. Pages matching no context get the default replies above, in the language the page asked for. A context's own replies are not per-language: they go out exactly as written, to every market this widget serves."}{" "}
            {translate("pageContextsSaveHint") ||
              "Each context saves on its own: the Save button below does not carry them."}
          </p>
        </div>
      </div>
    </div>
  );
};

export default CopySection;
